import { Fragment, useMemo, useContext, useCallback, useEffect } from "react"
import PropTypes from "prop-types"
import { compact, map, omit, pick, includes, isNil, get, first, chain, find } from "lodash"
import { useDispatch } from "react-redux"
import { Divider } from "@mui/material"
import { numberFormatter, percentageFormatter, scaledPercentageFormatter } from "helpers/formatters/numbers"
import DataIntegrator from "gatherers/data-integrator"
import CompareSection from "connected-components/compare/renderers/compare-section"
import { GMV, REFERENCE_EQUITY, POSITIONS, CONCENTRATION, MODEL_COVERAGE, MARKET_CAP, PREDICTED_BETA, HISTORICAL_BETA, CUSTOM_BETA } from "gatherers/constants"
import useCompositionTables, { positionSetHasNoGmvOrEquity } from "connected-components/compare/hooks/composition-tables"
import { createCompositionSingleDateQuery } from "gatherers/single-date/composition"
import { createRiskSingleDateQuery, SPECIFIC_RISK, FACTORS_RISK, TOTAL_RISK } from "gatherers/single-date/risk"
import { createPositionActionFn } from "connected-components/dashboard/helpers/positions"
import { useBaseWithSegment } from "connected-components/dashboard/helpers"
import CompareTable from "connected-components/compare/renderers/compare-table"
import PositionTooltip from "components/position-tooltip"
import { OK } from "helpers/data-statuses"
import { checkClassificationAccess } from "helpers/classification"
import { useAnalytics } from "connected-components/analytics"
import { RULES } from "connected-components/compare/position-sets"
import { KPIContext } from "../../context/kpi"
import { KPI_EVENTS } from "../../analytics"
import { addDeltaColumn } from "connected-components/shared/helpers/data-transforms"
import { useIsOpPro } from "resources/account"

export default function KPISummary({ modelMetadata, betaInstrument, classifications, context, instrumentsWithQueryOptions, instrumentsWithMetadata, positionSetsWithQueryOptions, positionSets, isIntradayMode, showDelta, availableGroups, getEffectiveEndDate, currentDate, intradayPriceRefreshTime }) {
  const isOpPro = useIsOpPro()
  const { sectionSettings, setSectionExpanded, selectedGroup, setSelectedGroup, isPreferencesLoaded } = useContext(KPIContext)
  const { currency = "USD", base, filter, normalization, isEndVsStartDateComparison, intradayBatchMetadata, endDate, startDate, period, selectedMode } = useContext(context)
  const baseWithSegment = useBaseWithSegment(base, filter, normalization)

  const analytics = useAnalytics()

  const availableOptions = useMemo(() => chain(availableGroups)
    .map(group => group?.options)
    .flatten()
    .value(), [availableGroups])
  const isSelectedGroupGICS = selectedGroup?.classificationId === "GICS"

  // Initialize selectedGroup if not set or if it doesn't have GICS classification access
  useEffect(() => {
    const hasNoSelectedGroup = isPreferencesLoaded && !selectedGroup
    const hasAvailableOptions = availableOptions.length > 0
    const shouldSetFirstOption = hasAvailableOptions && (hasNoSelectedGroup || (isSelectedGroupGICS && !checkClassificationAccess("GICS", classifications)))

    if (shouldSetFirstOption) {
      const firstOption = first(availableOptions)
      setSelectedGroup(firstOption)
      analytics.track(KPI_EVENTS.GROUP_SELECTED, {
        groupId: firstOption?.id,
        groupName: firstOption?.name,
        groupType: firstOption?.type,
      })
    }
  }, [selectedGroup, availableOptions, setSelectedGroup, isPreferencesLoaded, classifications, isSelectedGroupGICS, analytics])

  const dispatch = useDispatch()
  const compositionTableKey = "composition-table"

  const { comparison } = instrumentsWithQueryOptions
  const isRulesCompareMode = comparison?.type === RULES

  // Extend position sets with intraday options for summary-specific queries
  const positionSetsWithIntradayOptions = useMemo(() => {
    if (!isIntradayMode) {
      return positionSetsWithQueryOptions
    }

    return map(positionSetsWithQueryOptions, (positionSet, index) => {
      const originalPositionSet = positionSets[index]

      // Add intraday batch metadata for queries
      // Special case: comparison instrument in end vs start when dates match
      const isComparisonInEndVsStart = originalPositionSet === comparison && endDate === startDate
      const needsIntradayOptions = isComparisonInEndVsStart || originalPositionSet !== comparison || isRulesCompareMode

      if (needsIntradayOptions) {
        return {
          ...positionSet,
          isIntradayMode,
          batchId: intradayBatchMetadata?.id,
        }
      }

      return positionSet
    })
  }, [positionSetsWithQueryOptions, positionSets, comparison, isIntradayMode, intradayBatchMetadata, endDate, startDate, isRulesCompareMode])
  // Composition needs the model name to display beta tooltips
  const model = useMemo(() => pick(modelMetadata, ["id", "name"]), [modelMetadata])

  const betaMetrics = useMemo(() => {
    const baseMetrics = [PREDICTED_BETA, HISTORICAL_BETA].filter(metric => !includes(modelMetadata.missingDataFiles, metric))
    return betaInstrument ? [...baseMetrics, CUSTOM_BETA] : baseMetrics
  }, [modelMetadata.missingDataFiles, betaInstrument])

  const tableValueFormatter = useCallback((value) => isNil(value) ? "-" : scaledPercentageFormatter(value), [])
  const labelFormatter = useCallback((value) => value?.label || value?.description || "", [])
  const valueGetter = useCallback((value) => get(value, "economicExposure"), [])
  const positionFormatter = useCallback((position) => {
    let content = <a
      data-test-class="compare-table-link"
      href={`#${position.id}`}
      onClick={createPositionActionFn(
        position,
        dispatch,
        startDate && endDate ? { startDate, endDate, period } : undefined,
      )}
    >
      {position.label}
    </a>
    if (position.isUnmodeled) {
      content = <span data-test-class="compare-table-link">{position.label}</span>
    }
    return <PositionTooltip position={omit(position, ["economicExposure", "basePercentEquity", "positionPercentEquity"])}>{content}</PositionTooltip>
  }, [dispatch, startDate, endDate, period])

  const [groupsTable, positionsTable] = useCompositionTables({
    configKey: compositionTableKey,
    model,
    positionSets: positionSetsWithIntradayOptions,
    getEffectiveEndDate,
    currentDate,
    currency,
    selectedGroup,
    updateQueryString: ({ selectedGroup }) => {
      const option = find(availableOptions, ({ id }) => id === selectedGroup.id)
      if (option) {
        analytics.track(KPI_EVENTS.GROUP_SELECTED, {
          groupId: option?.id,
          groupName: option?.name,
          groupType: option?.type,
        })
        setSelectedGroup(option)
      }
    },
    classificationsStatus: OK,
    labelFormatter,
    positionFormatter,
    valueGetter,
    tableValueFormatter,
    availableGroups,
    classifications,
    renderer: CompareTable,
    gatherer: createCompositionSingleDateQuery,
    rowCount: 10,
  })

  const hasReferenceEquity = !isNil(instrumentsWithMetadata?.original.referenceEquity)
  const sections = useMemo(
    () => {
      const rawSections = compact([
        !isOpPro && {
          id: "risks",
          name: "Risk",
          separator: true,
          metrics: map(positionSetsWithIntradayOptions, (set, index) => createRiskSingleDateQuery(model.id, set.queryPositionSet, baseWithSegment, getEffectiveEndDate(index), {
            metrics: [TOTAL_RISK, SPECIFIC_RISK, FACTORS_RISK],
            isActive: Boolean(baseWithSegment),
            ...pick(set, ["isIntradayMode", "batchId"]),
          })),
          formatter: percentageFormatter,
          defaultExpanded: sectionSettings["risks"] ?? true,
          rowCount: 3,
        },
        {
          id: "composition",
          name: "Composition",
          separator: true,
          metrics: map(positionSetsWithIntradayOptions, (set, index) => createCompositionSingleDateQuery(model, set.queryPositionSet, baseWithSegment, getEffectiveEndDate(index), {
            type: "metrics",
            metrics: [GMV, REFERENCE_EQUITY, POSITIONS, CONCENTRATION, MODEL_COVERAGE, MARKET_CAP],
            currency,
            noGmvOrEquity: positionSetHasNoGmvOrEquity(set),
            hasReferenceEquity,
            ...pick(set, ["isIntradayMode", "batchId"]),
          })),
          formatter: numberFormatter,
          defaultExpanded: sectionSettings["composition"] ?? true,
          rowCount: 6,
        },
        {
          id: "beta",
          name: "Beta",
          alwaysMultipleRows: true,
          separator: true,
          metrics: map(positionSetsWithIntradayOptions, (set, index) => createCompositionSingleDateQuery(model, set.queryPositionSet, baseWithSegment, getEffectiveEndDate(index), {
            type: "metrics",
            metrics: betaMetrics,
            currency,
            noGmvOrEquity: positionSetHasNoGmvOrEquity(set),
            betaInstrument,
            ...pick(set, ["isIntradayMode", "batchId"]),
          })),
          formatter: numberFormatter,
          defaultExpanded: sectionSettings["beta"] ?? true,
          rowCount: 2,
        },
        groupsTable,
        {
          ...positionsTable,
          separator: false,
        },
      ])

      if (!intradayPriceRefreshTime) return rawSections

      return rawSections.map(section => {
        if (!section?.metrics) return section
        return {
          ...section,
          metrics: section.metrics.map(metric => {
            if (!metric?.key) return metric
            return { ...metric, key: [...metric.key, intradayPriceRefreshTime] }
          }),
        }
      })
    },
    [positionSetsWithIntradayOptions, model, currency, betaMetrics, baseWithSegment, betaInstrument, groupsTable, positionsTable, sectionSettings, getEffectiveEndDate, isOpPro, hasReferenceEquity, intradayPriceRefreshTime],
  )

  return map(sections, (section, index) => <Fragment key={`composition-${section.id}`}>
    <DataIntegrator
      dataTransform={showDelta ? (data) => addDeltaColumn(data, selectedMode) : undefined}
      metrics={section.metrics}
      Renderer={section.renderer || CompareSection}
      rendererProps={{
        queryState: sectionSettings?.[section.id] || { expanded: section.defaultExpanded, all: false },
        onQueryStateChange: (queryState) => {
          // Track section expansion/collapse
          analytics.track(KPI_EVENTS.SECTION_TOGGLED, {
            sectionName: section.id,
            newState: queryState,
          })
          setSectionExpanded({ key: section.id, value: queryState })
        },
        index,
        formatter: numberFormatter,
        configKey: `composition-${section.id}`,
        columnCount: showDelta ? 3 : positionSets.length,
        positionSetsMetadata: positionSets,
        instrumentsWithQueryOptions,
        instrumentsWithMetadata,
        isEndVsStartDateComparison,
        useSkeletonLoader: true,
        buttonSize: "small",
        ...omit(section, ["id", "metrics", "renderer"]),
        downloadSheetName: "KPI",
      }}
    />
    {section.separator && <Divider />}
  </Fragment>)
}

KPISummary.propTypes = {
  modelMetadata: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    missingDataFiles: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  betaInstrument: PropTypes.object,
  classifications: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  instrumentsWithQueryOptions: PropTypes.shape({
    original: PropTypes.object,
    rebalanced: PropTypes.object,
    comparison: PropTypes.object,
  }).isRequired,
  instrumentsWithMetadata: PropTypes.object.isRequired,
  positionSetsWithQueryOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  positionSets: PropTypes.arrayOf(PropTypes.object).isRequired,
  isIntradayMode: PropTypes.bool.isRequired,
  showDelta: PropTypes.bool.isRequired,
  availableGroups: PropTypes.array.isRequired,
  getEffectiveEndDate: PropTypes.func.isRequired,
  currentDate: PropTypes.instanceOf(Date).isRequired,
}
